/*
 * The difference between team property and property is now blurry because of recent changes and
 * ongoing discussions.
 * A team property was supposed to be a property that is shared across the team and can be used
 * in multiple classes (1 to many relation).
 * Properties are now 1 to 1 with classes, effectively removing the concept of team-level property.
 * https://www.notion.so/v7labs/Properties-implementation-revision-f1cee2147fef41f2a253c9070cf31091
 * Although this is likely to be the final implementation, the properties functionality will be
 * reviewed after the first beta release and a decision will be made.
 * Depending on the outcome, the TeamProperty type might become redundant and be removed.
 *
 * Ticket: https://linear.app/v7labs/issue/ANN-3021/refactor-property-types
 */
export const TeamPropertyType = {
  SINGLE_SELECT: 'single_select',
  MULTI_SELECT: 'multi_select',
  TEXT: 'text',
  AUDIO: 'audio',
} as const
export type TeamPropertyType = (typeof TeamPropertyType)[keyof typeof TeamPropertyType]

/**
 * Property granularity description:
 * `section`: normal property, at annotation level, that can be set per frame
 * `annotation`: normal annotation property, but the value will be static for the entire annotation,
 * so once changed in any frame, it will change on every frame (with a global value)
 * `item`: this property is set at item level, not related to annotations. It will appear on the
 * right sidebar as a "question"
 */
export const TeamPropertyGranularity = {
  SECTION: 'section',
  ITEM: 'item',
  ANNOTATION: 'annotation',
} as const
export type TeamPropertyGranularity =
  (typeof TeamPropertyGranularity)[keyof typeof TeamPropertyGranularity]

export interface TeamPropertyValue {
  id: string
  color: string
  position: number
  value: string
}

export const isTeamPropertyValue = (value: unknown): value is TeamPropertyValue =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  'color' in value &&
  'position' in value &&
  'value' in value

export interface TeamPropertyTextValue {
  text: string
}

export const isTeamPropertyTextValue = (value: unknown): value is TeamPropertyTextValue =>
  typeof value === 'object' && value !== null && 'text' in value

export interface TeamPropertyAudioValue {
  id: string
  duration_seconds: number
}

export const isTeamPropertyAudioValue = (value: unknown): value is TeamPropertyAudioValue =>
  typeof value === 'object' && value !== null && 'id' in value && 'duration_seconds' in value

/**
 * Maximum nesting depth for a property subtree. Enforced on both the backend
 * and the frontend when creating a nested property.
 */
export const MAX_PROPERTY_NESTING_DEPTH = 20

/**
 * Property types that participate in nested-property relationships, both as
 * a parent (whose values drive visibility) and as a child (created under a
 * parent with a `trigger_condition`). Mirrors the backend allow-list.
 *
 * Split this constant only if the parent/child allow-lists actually diverge.
 */
export const NESTABLE_PROPERTY_TYPES: readonly TeamPropertyType[] = [
  'single_select',
  'multi_select',
  'text',
]

export const TriggerConditionType = {
  VALUE_MATCH: 'value_match',
  ANY_VALUE: 'any_value',
} as const
export type TriggerConditionType = (typeof TriggerConditionType)[keyof typeof TriggerConditionType]

/**
 * Visibility condition attached to a nested property. When the parent property's
 * current value satisfies the condition, the child property is visible; otherwise
 * the child is hidden in the UI and excluded from required validation.
 */
export type TriggerCondition =
  | { type: 'value_match'; property_value_ids: string[] }
  | { type: 'any_value' }

export const isTriggerCondition = (value: unknown): value is TriggerCondition => {
  if (!value || typeof value !== 'object' || !('type' in value)) return false
  const type = (value as { type: unknown }).type
  if (type === TriggerConditionType.ANY_VALUE) return true
  if (type === TriggerConditionType.VALUE_MATCH) {
    const ids = (value as { property_value_ids?: unknown }).property_value_ids
    return Array.isArray(ids) && ids.every((id) => typeof id === 'string')
  }
  return false
}

export interface TeamProperty {
  id: string
  name: string
  description?: string
  annotation_class_id?: number
  dataset_ids?: number[]
  required: boolean
  property_values: TeamPropertyValue[]
  slug: string
  team_id: number
  type: TeamPropertyType
  granularity: TeamPropertyGranularity
  position: number | null
  inserted_at: string
  /**
   * Id of the parent property. `null` for top-level properties.
   */
  parent_property_id: string | null
  /**
   * Condition that must be met on the parent for this property to be visible.
   * `null` for top-level properties.
   */
  trigger_condition: TriggerCondition | null
  /**
   * Minimum recording duration (in seconds) for `audio` properties.
   * `null`/absent means no lower bound.
   */
  min_duration_seconds?: number | null
  /**
   * Maximum recording duration (in seconds) for `audio` properties.
   * `null`/absent means no upper bound.
   */
  max_duration_seconds?: number | null
}

export type TeamItemProperty = Omit<TeamProperty, 'annotation_class_id'> & {
  dataset_ids: number[]
  granularity: 'item'
}

export type TeamAnnotationProperty = TeamProperty & {
  values: (TeamPropertyValue | TeamPropertyTextValue)[]
}

export type Property = Pick<
  TeamProperty,
  | 'id'
  | 'name'
  | 'type'
  | 'required'
  | 'annotation_class_id'
  | 'description'
  | 'granularity'
  | 'dataset_ids'
  | 'position'
  | 'inserted_at'
  | 'parent_property_id'
  | 'trigger_condition'
  | 'min_duration_seconds'
  | 'max_duration_seconds'
> & {
  values: PropertyValue[]
}

export type UnsavedProperty = Omit<Property, 'id' | 'annotation_class_id' | 'inserted_at'> & {
  annotation_class_id?: number
}

/**
 * Returns true if the given property type is allowed to be a parent in a
 * nested-property relationship.
 */
export const canBeParentProperty = (type: TeamPropertyType): boolean =>
  (NESTABLE_PROPERTY_TYPES as readonly TeamPropertyType[]).includes(type)

// This type is for the dummy property component that is used to create a new property
// For this reason it can include both unsaved properties or real properties
export type MaybeProperty = Property | UnsavedProperty

export type PropertyValue = Pick<TeamPropertyValue, 'id'> & {
  name: TeamPropertyValue['value']
  color: string
  position: number
  error?: string
}

export type UnsavedPropertyValue = Partial<PropertyValue> & {
  id: TeamPropertyValue['id']
  dummy?: boolean
}

// This type is for the dummy property value component that is used to create new property values
// For this reason it can include both unsaved property values or real property values
export type MaybePropertyValue = PropertyValue | UnsavedPropertyValue

export const isPropertyValue = (propertyValue: unknown): propertyValue is PropertyValue =>
  !!propertyValue &&
  typeof propertyValue === 'object' &&
  'id' in propertyValue &&
  'name' in propertyValue &&
  'color' in propertyValue &&
  'position' in propertyValue

export const isTeamPropertyType = (propertyType: string): propertyType is TeamPropertyType =>
  propertyType === TeamPropertyType.MULTI_SELECT ||
  propertyType === TeamPropertyType.SINGLE_SELECT ||
  propertyType === TeamPropertyType.TEXT ||
  propertyType === TeamPropertyType.AUDIO

export const isTeamProperty = (property: unknown): property is TeamProperty =>
  !!property &&
  typeof property === 'object' &&
  'type' in property &&
  isTeamPropertyType(property.type as string)

export const isTeamItemProperty = (property: unknown): property is TeamItemProperty =>
  isTeamProperty(property) && property.granularity === TeamPropertyGranularity.ITEM
